import { ArrowBackOutline } from '@styled-icons/evaicons-outline/ArrowBackOutline'
import debounce from 'debounce'
import * as React from 'react'

import {
  convertUnitRecordToString,
  isResponseValid,
  parseFormattedUnitResponseString,
} from '@app/domain/form/form.helpers'
import type { UnitRecord } from '@app/domain/form/form.interface'
import type { Question } from '@app/domain/instrument'
import type { IEntry } from '@app/domain/instrument/instrument.interface'
import { QuestionTypeId } from '@app/domain/instrument/instrument.interface'
import type { VisitStatusId } from '@app/domain/visit/visit.interface'
import { hasContent } from '@app/lib/string'
import type { QuestionsConfig } from '@app/state/config/interfaces'

import {
  FooterRow,
  FooterScrollTextContainer,
  FormQuestions,
  HeaderContainer,
  ScreenContent,
  InstrumentScreenContent,
  ScreenHeader,
} from './content-layout'
import { BackButton, ContinueButton, Control, ScrollDown } from './controls'
import {
  FormGroup,
  InputVariant,
  MultiSelect,
  Radio,
  RadioVariant,
  Scale,
} from './form-inputs'
import { DateQuestion } from './input/date-question'
import { TextQuestion } from './input/text-question'
import { UnitQuestion } from './input/unit-question'
import { VisualAnalogScale } from './input/visual-analog-scale'

interface IntakeFormProps {
  header?: string
  content?: string
  entry?: IEntry
  instrumentId: string
  questions: Question[]
  questionsConfig: QuestionsConfig
  display?: Record<string, boolean>
  evaluateDisplayLogic?: (
    instrumentId: string,
    questionId?: string,
    response?: string,
  ) => void
  handleContinue?: () => void
  handleGoBack?: () => void
  handleSubmit?: (
    instrumentId: string,
    responses: Record<string, string>,
    preventNextScreen?: boolean,
  ) => void
  visitStatus: VisitStatusId
}

interface IntakeFormState {
  responses: Record<string, string>
  displayScrollBottom: boolean
}

export class IntakeForm extends React.Component<
  IntakeFormProps,
  IntakeFormState
> {
  debouncedOnResponseChange: (questionId: string, value: string) => void
  constructor(props) {
    super(props)
    this.state = {
      responses: this.props.entry ? this.props.entry.responses : {},
      displayScrollBottom: false,
    }

    this.debouncedOnResponseChange = debounce(this.handleResponseChange, 500)
  }

  componentDidMount() {
    const pageBackground = document.querySelector('.page-background')
    if (pageBackground) {
      pageBackground.addEventListener('scroll', this.handleScroll)
      this.handleScroll()
    }
  }

  componentWillUnmount() {
    const pageBackground = document.querySelector('.page-background')
    if (pageBackground) {
      pageBackground.removeEventListener('scroll', this.handleScroll)
    }
    this.setState({
      displayScrollBottom: false,
    })
  }

  handleScroll = () => {
    const scrollParent = document.querySelector('.page-background')
    const { scrollTop, scrollHeight, clientHeight } = scrollParent
    const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) <= 1

    this.setState({
      displayScrollBottom: !atBottom,
    })
  }

  componentDidUpdate(nextProps) {
    const { entry } = this.props
    if (nextProps.entry !== entry) {
      this.setState({
        responses: this.props.entry ? this.props.entry.responses : {},
      })
    }
  }

  renderBackButton = (goBack?: () => void) => {
    if (goBack) {
      return (
        <BackButton onClick={goBack}>
          <ArrowBackOutline size="45" />
        </BackButton>
      )
    } else {
      return <></>
    }
  }

  renderHeader = (header?: string) => {
    if (!header) {
      return null
    }

    return (
      <HeaderContainer>
        <ScreenHeader dangerouslySetInnerHTML={{ __html: header }} />
      </HeaderContainer>
    )
  }

  renderContent = (content?: string) => {
    if (content) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />
    } else {
      return null
    }
  }

  checkQuestionWithUnitResponse = (question: Question): boolean => {
    if (this.state.responses[question.id]) {
      const value = parseFormattedUnitResponseString(
        this.state.responses[question.id],
      )
      if (
        (question.required &&
          this.shouldDisplay(question.id) &&
          !hasContent(value?.value)) ||
        !hasContent(value?.unit) ||
        (question.required &&
          this.shouldDisplay(question.id) &&
          !isResponseValid(question, value?.value, this.props.questionsConfig))
      ) {
        return false
      }

      //regardless if response is required or not, if there is user input, validate.
      return (
        isResponseValid(question, value?.value, this.props.questionsConfig) &&
        hasContent(value?.unit)
      )
    }
    // there is no response and the question was required/should display
    if (question.required || this.shouldDisplay(question.id)) {
      return false
    }

    //no reponse and not required
    return true
  }

  submitCurrentQuestion = (questionId: string) => {
    const question = this.props.questions.find((x) => x.id === questionId)

    let isValid: boolean

    if (question.inputType === QuestionTypeId.NumericWithUnits) {
      isValid = this.checkQuestionWithUnitResponse(question)
    } else {
      isValid = isResponseValid(
        this.props.questions.find((x) => x.id === questionId),
        this.state.responses[questionId],
        this.props.questionsConfig,
      )
    }

    const responses = Object.keys(this.state.responses).reduce(
      (accumulator, qid) => {
        if (this.shouldDisplay(qid)) {
          return {
            ...accumulator,
            [qid]: this.state.responses[qid],
          }
        } else {
          return accumulator
        }
      },
      {},
    )

    if (isValid) {
      this.props.handleSubmit(this.props.entry.id, responses, true)
    }
  }

  handleResponseChange = (question: string, value: string) => {
    this.props.evaluateDisplayLogic(this.props.instrumentId, question, value)
    this.setState(
      {
        responses: {
          ...this.state.responses,
          [question]: value,
        },
      },
      () => {
        this.submitCurrentQuestion(question)
      },
    )
  }

  handleUnitQuestionResponseChange = (question: string, value: UnitRecord) => {
    this.props.evaluateDisplayLogic(
      this.props.instrumentId,
      question,
      value.value,
    )

    const formattedString =
      hasContent(value.value) && hasContent(value.unit)
        ? convertUnitRecordToString(value)
        : ''
    this.setState(
      {
        responses: {
          ...this.state.responses,
          [question]: formattedString,
        },
      },
      () => {
        this.submitCurrentQuestion(question)
      },
    )
  }

  handleCheckboxResponseChange = (
    question: string,
    responses: string[],
    value: string,
  ) => {
    if (responses.findIndex((r) => r === value) >= 0) {
      responses = responses.filter((r) => r !== value)
    } else {
      responses.push(value)
    }
    let response = responses.join('|')
    if (response === '') {
      response = undefined
    }
    this.props.evaluateDisplayLogic(this.props.instrumentId, question, response)
    this.setState(
      {
        responses: {
          ...this.state.responses,
          [question]: response,
        },
      },
      () => {
        this.submitCurrentQuestion(question)
      },
    )
  }

  clearResponse = (question: string) => {
    this.setState({
      responses: {
        ...this.state.responses,
        [question]: null,
      },
    })
  }

  shouldDisplay = (questionId: string) => {
    const { display } = this.props
    if (!display) {
      return true
    } else if (display && questionId in display) {
      return display[questionId]
    } else {
      return true
    }
  }

  renderQuestion = (question: Question, key: number) => {
    let parsedData = {}
    if (this.shouldDisplay(question.id)) {
      switch (question.inputType) {
        case 'text':
          return (
            <TextQuestion
              key={question.id}
              question={question}
              value={this.state.responses[question.id]}
              onChange={(value) => {
                this.handleResponseChange(question.id, value)
              }}
            />
          )
        case 'numeric-with-units':
          if (this.state.responses[question.id]) {
            parsedData = parseFormattedUnitResponseString(
              this.state.responses[question.id],
            )
          }

          return (
            <UnitQuestion
              key={question.id}
              question={question}
              value={parsedData}
              onChange={(value) => {
                this.handleUnitQuestionResponseChange(question.id, value)
              }}
            />
          )
        case 'date':
          return (
            <DateQuestion
              key={key}
              onChange={(value) => {
                this.handleResponseChange(question.id, value)
              }}
              question={question}
              value={this.state.responses[question.id]}
            />
          )

        case 'checkbox': {
          let responses = []
          if (this.state.responses[question.id]) {
            responses = this.state.responses[question.id].split('|')
          }
          return (
            <FormGroup
              key={`question-${key}`}
              fieldId={question.id}
              label={question.label}
              details={question.details}
            >
              {question.options.map((option) => (
                <MultiSelect
                  id={`${question.id}_${option.value}`}
                  key={`${question.id}_${option.value}`}
                  label={option.label}
                  checked={
                    responses.findIndex((v) => v === option.value) !== -1
                  }
                  name={question.id}
                  value={option.value}
                  onChange={(event) => {
                    this.handleCheckboxResponseChange(
                      question.id,
                      responses,
                      event.currentTarget.value,
                    )
                  }}
                />
              ))}
            </FormGroup>
          )
        }
        case 'radio': {
          return (
            <FormGroup
              key={`question-${key}`}
              fieldId={question.id}
              label={question.label}
              details={question.details}
            >
              {question.options.map((option) => (
                <Radio
                  id={`${question.id}_${option.value}`}
                  key={`${question.id}_${option.value}`}
                  label={option.label}
                  checked={this.state.responses[question.id] === option.value}
                  name={question.id}
                  value={option.value}
                  onChange={(event) => {
                    this.handleResponseChange(
                      question.id,
                      event.currentTarget.value,
                    )
                  }}
                />
              ))}
            </FormGroup>
          )
        }
        case 'binary': {
          return (
            <FormGroup
              key={`question-${key}`}
              fieldId={question.id}
              label={question.label}
              details={question.details}
              variant={InputVariant.Horizontal}
            >
              {question.options.map((option) => (
                <Radio
                  variant={RadioVariant.Grid}
                  id={`${question.id}_${option.value}`}
                  key={`${question.id}_${option.value}`}
                  label={option.label}
                  checked={this.state.responses[question.id] === option.value}
                  name={question.id}
                  value={option.value}
                  onChange={(event) => {
                    this.handleResponseChange(
                      question.id,
                      event.currentTarget.value,
                    )
                  }}
                />
              ))}
            </FormGroup>
          )
        }
        case 'scale': {
          return (
            <FormGroup
              key={`question-${key}`}
              fieldId={question.id}
              label={question.label}
              details={question.details}
              stretch={`stretch`}
            >
              <Scale
                key={question.id}
                id={`${question.id}`}
                name={question.id}
                value={this.state.responses[question.id]}
                onChange={(event) => {
                  const value = event.currentTarget.value
                  this.setState((prevState) => ({
                    responses: {
                      ...prevState.responses,
                      [question.id]: value,
                    },
                  }))

                  this.debouncedOnResponseChange(question.id, value)
                }}
              />
            </FormGroup>
          )
        }
        case 'visual_analog_scale': {
          return (
            <FormGroup
              key={`question-${key}`}
              fieldId={question.id}
              label={question.label}
              details={question.details}
              stretch={`stretch`}
            >
              <VisualAnalogScale
                key={question.id}
                id={`${question.id}`}
                name={question.id}
                value={this.state.responses[question.id]}
                onChange={(event) => {
                  const value = event.currentTarget.value
                  this.setState((prevState) => ({
                    responses: {
                      ...prevState.responses,
                      [question.id]: value,
                    },
                  }))

                  this.debouncedOnResponseChange(question.id, value)
                }}
              />
            </FormGroup>
          )
        }
        default:
          return <React.Fragment key={key} />
      }
    } else {
      // this.clearResponse(question.id)
      return <React.Fragment key={key} />
    }
  }

  checkResponse = (question: Question): boolean => {
    if (
      question.required &&
      this.shouldDisplay(question.id) &&
      !isResponseValid(
        question,
        this.state.responses[question.id],
        this.props.questionsConfig,
      )
    ) {
      return false
    } else {
      if (
        question.required &&
        this.shouldDisplay(question.id) &&
        !this.state.responses[question.id]
      ) {
        return false
      } else {
        //regardless if response is required or not, if there is user input, validate.
        if (this.state.responses[question.id]) {
          return isResponseValid(
            question,
            this.state.responses[question.id],
            this.props.questionsConfig,
          )
        }
        return true
      }
    }
  }

  isValid = (questions: Question[]): boolean => {
    return questions.every((question) => {
      if (question.inputType === QuestionTypeId.NumericWithUnits) {
        return this.checkQuestionWithUnitResponse(question)
      } else {
        return this.checkResponse(question)
      }
    })
  }

  renderContinueButton = (questions: Question[]) => {
    if (this.props.handleSubmit && this.isValid(questions)) {
      return (
        <ContinueButton
          onClick={() =>
            this.props.handleSubmit(this.props.entry.id, this.state.responses)
          }
        >
          Continue
        </ContinueButton>
      )
    } else {
      return <></>
    }
  }

  render() {
    const { handleGoBack, header, questions, content } = this.props
    return (
      <ScreenContent>
        {this.renderHeader(header)}
        <InstrumentScreenContent>
          {this.renderContent(content)}
        </InstrumentScreenContent>
        <FormQuestions style={{ width: '90%' }}>
          {questions.map((question, idx) => this.renderQuestion(question, idx))}
        </FormQuestions>
        <FooterRow>
          <Control>{this.renderBackButton(handleGoBack)}</Control>
          <Control>{this.renderContinueButton(questions)}</Control>
          <Control>
            {this.state.displayScrollBottom ? (
              <>
                <FooterScrollTextContainer>
                  <ScrollDown />
                  Scroll Down for More
                </FooterScrollTextContainer>
              </>
            ) : null}
          </Control>
        </FooterRow>
      </ScreenContent>
    )
  }
}
